/**
 * App-wide live sync for AI ("Velo") server-side mutations.
 *
 * Velo's tools run SERVER-SIDE (loopback REST), so when one mutates data the
 * browser never sees the write and open pages go stale until a manual reload.
 * `AiAssistantProvider` dispatches a single generic window event
 * ({@link AI_DATA_MUTATED_EVENT}) carrying the tool name + event id whenever a
 * server tool completes successfully. This module is the SINGLE SOURCE OF TRUTH
 * that maps a tool name to the data domains it can affect, so consumers (the
 * central react-query bridge + per-store hooks) know what to refresh.
 *
 * All AI tools are event-scoped, so `eventId` is always present and consumers
 * match it against their route event id.
 */

/** Window CustomEvent name fired by AiAssistantProvider on a successful server tool. */
export const AI_DATA_MUTATED_EVENT = "veltric:ai-data-mutated";

export type AiDataMutatedDetail = {
  toolName: string;
  eventId: string | null;
};

/** Data domains an AI mutation can affect. Used to route refreshes. */
export type SyncDomain =
  | "guests"
  | "seating"
  | "slotting"
  | "dashboard"
  | "emailTemplates"
  | "sentEmails"
  | "workflows"
  | "checkinDevices"
  | "checkinRules"
  | "attachments"
  | "additionalFields"
  | "documents";

// ── Tool → affected domains (explicit, authoritative) ────────────────────────
// Keyed by the exact @AiTool names from the backend. Cascades are DATA-centric
// (what data the write touches), not "where the tool lives": e.g. a guest edit
// also changes seat-occupant names (seating), appointment guests (slotting) and
// rollups (dashboard). Read-only tools (list_*/get_*/suggest_slots) and pure
// background-job tools (export_guests) are intentionally absent — no live data
// to reconcile. Keep in sync with the backend @AiTool set; the keyword fallback
// + DEV warning below catch anything added here that we forget to map.

const GUEST_CASCADE: SyncDomain[] = ["guests", "seating", "slotting", "dashboard"];

/**
 * Exported so a guard test can hold it against the i18n labels: every tool this
 * map knows is a tool the assistant can run, and a tool the assistant can run
 * must have a name a human can read.
 */
export const TOOL_DOMAINS: Record<string, SyncDomain[]> = {
  // Guests (cascade: occupant names + appointment guests + rollups)
  create_guest: GUEST_CASCADE,
  update_guest: GUEST_CASCADE,
  delete_guests: GUEST_CASCADE,
  update_guests_by_filter: GUEST_CASCADE,
  check_in_guest: GUEST_CASCADE,
  batch_check_in_guests: GUEST_CASCADE,
  promote_appointment_waitlist: ["slotting", "guests", "dashboard"],
  promote_waitlisted_guests: GUEST_CASCADE,
  enable_disable_workflow: ["workflows"],

  // Seating
  create_seating_furniture: ["seating"],
  create_seating_furniture_bulk: ["seating"],
  update_seating_furniture: ["seating"],
  delete_seating_furniture: ["seating"],
  resize_seating_furniture: ["seating"],
  create_seating_room: ["seating"],
  update_seating_room: ["seating"],
  delete_seating_room: ["seating"],
  assign_seat: ["seating", "guests", "dashboard"],
  // Frees every occupied chair of one table: the plan changes, every one of
  // those guests is unseated in the guest list, and the rollups on the
  // dashboard move with them.
  clear_seating_furniture_seats: ["seating", "guests", "dashboard"],

  // Slotting (appointments/lanes already live via WebSocket; offering/treatment
  // structural changes also touch dashboard rollups)
  create_appointment: ["slotting"],
  update_appointment: ["slotting"],
  cancel_appointment: ["slotting"],
  add_appointment_guests: ["slotting", "guests"],
  remove_appointment_guest: ["slotting", "guests"],
  checkin_appointment_guest: ["slotting", "guests", "dashboard"],
  noshow_appointment_guest: ["slotting", "guests", "dashboard"],
  upsert_lanes: ["slotting", "dashboard"],
  delete_lane: ["slotting", "dashboard"],
  create_offering: ["slotting", "dashboard"],
  update_offering: ["slotting", "dashboard"],
  delete_offering: ["slotting", "dashboard"],
  upsert_treatments: ["slotting", "dashboard"],
  delete_treatment: ["slotting", "dashboard"],

  // Email
  send_email_campaign: ["sentEmails", "guests", "dashboard"],
  update_email_template: ["emailTemplates"],
  delete_email_template: ["emailTemplates"],

  // Workflows
  create_workflow: ["workflows"],
  update_workflow: ["workflows"],
  delete_workflow: ["workflows"],

  // Dashboards
  create_dashboard: ["dashboard"],
  rename_dashboard: ["dashboard"],
  delete_dashboard: ["dashboard"],
  create_dashboard_card: ["dashboard"],
  update_dashboard_cards: ["dashboard"],
  delete_dashboard_card: ["dashboard"],

  // Check-in
  create_checkin_device: ["checkinDevices"],
  delete_checkin_device: ["checkinDevices"],
  upsert_checkin_rule: ["checkinRules"],

  // Additional fields (cascade: guest columns/schema)
  create_additional_field: ["additionalFields", "guests"],
  update_additional_field: ["additionalFields", "guests"],
  delete_additional_field: ["additionalFields", "guests"],

  // Attachments / portal links
  create_attachment: ["attachments"],
  update_attachment: ["attachments"],
  delete_attachment: ["attachments"],
  generate_portal_link: ["attachments"],
  revoke_portal_token: ["attachments"],

  // Documents
  delete_document_template: ["documents"],

  // duplicate_event creates a NEW event — it doesn't change the CURRENT event's
  // data, and the event-switcher list is not event-scoped (so the bridge's
  // eventId predicate can't target it). No live refresh; the event list's
  // staleTime + refetch-on-mount covers the rare case. Mapped explicitly to []
  // so it doesn't trip the "unmapped tool" DEV warning.
  duplicate_event: [],

  // Create-event wizard tools (surface wizard:event-create): read/apply operate
  // on the in-browser draft (CLIENT lane), set_event_brief writes the durable
  // conversation brief. None touch server-side event data — nothing to
  // reconcile. Mapped to [] so they don't trip the "unmapped tool" DEV warning.
  read_event_wizard_state: [],
  apply_event_wizard_state: [],
  set_event_brief: [],

  // ── CLIENT-lane tools (AiToolBridge handlers) ──────────────────────────────
  // executeClientTool now dispatches AI_DATA_MUTATED_EVENT on success (parity
  // with the server lane), so every registered client tool needs a mapping —
  // otherwise the keyword fallback misfires on local-only tools (e.g.
  // apply_guest_filter would refetch the whole guest cascade).
  // Server-mutating client tools:
  create_document_from_preset: ["documents"],
  generate_document_from_blocks: ["documents"],
  create_template_from_preset: ["emailTemplates"],
  generate_email_from_blocks: ["emailTemplates"],
  update_ai_template_slots: ["emailTemplates"],
  submit_slotting_wizard: ["slotting", "dashboard"],
  submit_workflow_wizard: ["workflows"],
  // generate_website_from_blocks / submit_website_wizard / submit_app_wizard /
  // submit_goals_wizard invalidate their own caches in-tool (website keys /
  // events.detail / goals doc) — nothing generic to add here.
  generate_website_from_blocks: [],
  submit_website_wizard: [],
  submit_app_wizard: [],
  submit_goals_wizard: [],
  // import submits a BACKGROUND job — the importGuestsCompleted event owns the
  // refresh when the job lands; invalidating at submit would be premature.
  import_guests_from_spreadsheet: [],
  // Purely page-local / read-only client tools:
  activate_guest_view: [],
  apply_guest_filter: [],
  clear_guest_filter: [],
  open_guest_profile: [],
  navigate_to_page: [],
  escalate_to_human: [],
  report_bug: [],
  read_attached_spreadsheet: [],
  read_app_wizard_state: [],
  apply_app_wizard_state: [],
  read_document_wizard_state: [],
  apply_document_wizard_state: [],
  read_email_wizard_state: [],
  apply_email_wizard_state: [],
  read_goals_state: [],
  read_goals_wizard_state: [],
  apply_goals_wizard_state: [],
  apply_goal_kpis: [],
  read_slotting_wizard_state: [],
  apply_slotting_wizard_state: [],
  read_website_wizard_state: [],
  apply_website_wizard_state: [],
  read_workflow_wizard_state: [],
  apply_workflow_wizard_state: [],
  read_website_builder_canvas: [],
  apply_blocks_to_website_builder: [],
  edit_website_builder_blocks: [],
  upload_attached_image_to_website_builder: [],
  read_website_theme: [],
  apply_website_theme: [],
  read_calendar_builder_config: [],
  apply_calendar_builder_config: [],
  read_portal_builder_config: [],
  apply_portal_builder_config: [],
  read_wallet_builder_config: [],
  apply_wallet_builder_config: [],
  get_preset_slots: [],
  list_template_presets: [],
  open_template_in_builder: [],
};

// ── Keyword fallback (resilience for unmapped / renamed tools) ────────────────
// Ordered: the FIRST matching rule wins, so collisions are resolved
// deterministically (e.g. "document" before generic "template"). Only used when
// a tool is missing from TOOL_DOMAINS; in DEV we also warn so it gets mapped.

const KEYWORD_RULES: Array<{ test: RegExp; domains: SyncDomain[] }> = [
  { test: /guest/, domains: GUEST_CASCADE },
  { test: /seat|furniture|room|table/, domains: ["seating"] },
  { test: /appointment|offering|lane|treatment|slot/, domains: ["slotting", "dashboard"] },
  { test: /campaign|sent_email|send_email/, domains: ["sentEmails", "guests", "dashboard"] },
  { test: /document/, domains: ["documents"] },
  { test: /email_template|template/, domains: ["emailTemplates"] },
  { test: /workflow/, domains: ["workflows"] },
  { test: /dashboard|card/, domains: ["dashboard"] },
  { test: /checkin_rule|check_in_rule/, domains: ["checkinRules"] },
  { test: /device|checkin|check_in/, domains: ["checkinDevices"] },
  { test: /portal|attachment|link|token/, domains: ["attachments"] },
  { test: /field/, domains: ["additionalFields", "guests"] },
];

/**
 * Map an AI tool name to the data domains its mutation can affect. Read-only
 * tools return an empty array. Falls back to keyword matching (with a DEV warn)
 * for any name not in the explicit map.
 */
export function resolveSyncDomains(toolName: string): SyncDomain[] {
  const explicit = TOOL_DOMAINS[toolName];
  if (explicit) return explicit;

  // Read-only / non-mutating tools never need a refresh.
  if (
    /^(list_|get_|search_|find_|count_)/.test(toolName) ||
    toolName === "suggest_slots" ||
    toolName === "export_guests" ||
    toolName === "navigate_to_page" ||
    toolName === "open_guest_profile"
  ) {
    return [];
  }

  for (const rule of KEYWORD_RULES) {
    if (rule.test.test(toolName)) {
      if (import.meta.env.DEV) {
        console.warn(
          `[ai-sync] tool "${toolName}" is not in TOOL_DOMAINS; using keyword fallback → ` +
            `[${rule.domains.join(", ")}]. Add it to the explicit map.`,
        );
      }
      return rule.domains;
    }
  }

  if (import.meta.env.DEV) {
    console.warn(`[ai-sync] tool "${toolName}" is unmapped and matched no keyword — no live refresh.`);
  }
  return [];
}

// ── Domain → react-query key prefixes (for the central invalidation bridge) ───
// Verified against the codebase: every event query key is `[prefix, eventId, …]`
// (eventId at index 1). `seating` and `slotting` are DELIBERATELY ABSENT even
// though `["seating", eventId]` / `["slotting", eventId]` route-loader queries
// exist: invalidating them would refetch the loader → feed a new `initialData`
// prop → re-run `initialize()`, which RESETS UI state (active room, zoom, pan,
// undo). Those two domains are owned by their custom-store hook / WebSocket.

export const DOMAIN_QUERY_PREFIXES: Partial<Record<SyncDomain, string[]>> = {
  guests: ["guestlist", "guestsRequests", "eventLayout"],
  dashboard: ["eventDashboard"],
  // The bulk templates page (msgMail) AND the open email builder (emailTemplate
  // / emailTemplatesByEvent) both reflect update_/delete_email_template.
  emailTemplates: ["msgMail", "emailTemplate", "emailTemplatesByEvent"],
  sentEmails: ["sentMails"],
  workflows: ["eventAutomationWorkflows", "eventAutomationHistory"],
  checkinDevices: ["checkInDevices", "checkInHistory"],
  checkinRules: ["checkInDevices"],
  attachments: ["eventAttachments", "eventAttachmentsSharedLinks"],
  // `aiFieldGate` is Velo's own read of the field definitions (goals client
  // tools, `useAiFieldGate`): a field Velo just created is otherwise
  // "protected" to those tools until a focus or remount (audit r3 LOW-1).
  additionalFields: ["additionalFields", "guestlist", "aiFieldGate"],
  // The documents list page AND the doc/calendar/wallet builders (which read the
  // same templates) reflect delete_document_template.
  documents: ["eventDocuments", "documentTemplate", "documentTemplates", "documentTemplateFull"],
};

/** Collect the react-query key prefixes affected by these domains (deduped). */
export function queryPrefixesForDomains(domains: readonly SyncDomain[]): Set<string> {
  const prefixes = new Set<string>();
  for (const domain of domains) {
    for (const p of DOMAIN_QUERY_PREFIXES[domain] ?? []) prefixes.add(p);
  }
  return prefixes;
}

/** Collect the react-query key prefixes affected by a tool (deduped). */
export function queryPrefixesForTool(toolName: string): Set<string> {
  return queryPrefixesForDomains(resolveSyncDomains(toolName));
}

/**
 * B2: a field-definition write (create / update / delete_additional_field)
 * runs the backend's revoke cascades on the event's role templates — the key
 * is pulled and each touched template's `version` bumped, so every cached
 * template is stale (a stale `version` is a 409 on the next save). Those reads
 * are not routable by their first key segment (`["eventSettings", eventId,
 * "roleTemplates"]` shares "eventSettings" with unrelated settings), so the
 * bridge matches them by shape when this is true.
 */
export function cascadesToRoleTemplates(domains: readonly SyncDomain[]): boolean {
  return domains.includes("additionalFields");
}
